/**
 * Renders the Evidence sub-agent inline, attached to its CHILD harness run.
 * Split into an outer gate + inner lifecycle so `useRunStreamLifecycle` is
 * never called above its mount condition (`allowMount` gates one level deep,
 * never a grandchild stream). Chromeless — the timeline is the UI.
 */

import type { ChatItem } from '@heidi/agents/contracts';
import { projectRunFailureMessages } from '@heidi/agents/data';
import { toast } from '@heidi/design-system/toast';
import type { VisualDxDiagnosticFlowSelections } from '@heidi/evidence';
import { logger, sanitizeError } from '@heidi/runtime/logger';
import { cn } from '@heidi/utils';
import { useQueryUser } from '@heidi/workspace/data';
import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { useIsWorkEvidenceDisclaimerEnabled } from '../../../../flags';
import { agentHarnessClient } from '../../../agent-harness/client';
import {
  useMutateResolveApproval,
  useRunStreamLifecycle,
} from '../../../agent-harness/hooks';
import { getWorkHostPort } from '../../../work-home/work-home-ports';
import type { EvidenceSessionStatus } from '../EvidenceSessionInlineBlock';
import { EvidenceChildTimeline } from './EvidenceChildTimeline';
import {
  CancelledNotice,
  ErrorNotice,
  StartingPlaceholder,
} from './EvidenceInlineStatus';
import { buildRegistryFromItems } from './evidenceChildData';

const visualDxSubmitErrorMessage = {
  id: 'lOqGE3VHxL',
  defaultMessage: 'Could not submit the VisualDx selections. Please try again.',
  description: 'Error shown when an Evidence child cannot resume after VisualDx input',
};

export interface EvidenceChildRunInlineProps {
  /** The evidence child run id, persisted on the parent tool row + live via `tool_execution_start`. */
  childAgentRunId: string;
  /** Coarse status derived from the parent tool row (running/success/error/cancelled). */
  status: EvidenceSessionStatus;
  /** Question the parent passed in — shown in the starting placeholder. */
  question?: string;
  /** False in a nested renderer so a nested Evidence call never opens a grandchild stream. */
  allowMount: boolean;
  /** Optional reply actions supplied by the parent chat. */
  renderReplyActions?: (copyableText: string | null) => ReactNode;
  /** The parent's Working row lists the searches and sources, so the timeline draws none. */
  searchesInWorkingBlock?: boolean;
  /**
   * Hands the child's transcript, as this card streams it, to the parent's
   * Working row, which shows the same step's progress; null once the card has
   * nothing to show or unmounts. The card is the child stream's only reader.
   */
  onLiveItems?: (childAgentRunId: string, items: readonly ChatItem[] | null) => void;
  className?: string;
}

/**
 * Whether the Work surface owns the Evidence disclaimer for this clinician.
 * UK/EU and NHS clinicians keep the legacy `/scribe/chat` gates, whose copy is
 * region-specific (research-tool positioning, NHS information governance) and
 * whose acknowledgement must not be satisfiable by the Work wording. Both gates
 * share `has_seen_evidence_disclaimer`, so keeping those cohorts out here is
 * what stops one acknowledgement standing in for the other.
 */
const isWorkEvidenceDisclaimerCohort = ({
  isUKOrEU,
  isNhsUser,
}: {
  isUKOrEU: boolean;
  isNhsUser: boolean;
}): boolean => !isUKOrEU && !isNhsUser;

/**
 * Opens the Evidence disclaimer the first time Work hands a question to the
 * Evidence sub-agent. Keyed on the same `has_seen_evidence_disclaimer` flag the
 * legacy `/scribe/chat` gates write, so a clinician who acknowledged it there
 * is never asked again here. Holds while the flag, region or user data is
 * pending (no flash) and while another drawer owns the screen; the accept path
 * writes the flag optimistically, so later Evidence blocks in the same session
 * stay quiet.
 */
const useWorkEvidenceDisclaimerPrompt = (): void => {
  const isEnabled = useIsWorkEvidenceDisclaimerEnabled();
  const { isUKOrEU, isPending: isRegionPending } = getWorkHostPort('shell').useIsUKOrEU();
  const { isNhsUser, isPending: isAccessPending } =
    getWorkHostPort('evidence').useEvidenceAccess();
  const { data: disclaimer } = useQueryUser((data) => ({
    hasSeen: Boolean(data?.evidence_settings?.has_seen_evidence_disclaimer),
  }));
  const isAnotherDrawerOpen = getWorkHostPort('shell').useAnyDialogDrawerOpen();
  const openDisclaimer = getWorkHostPort('evidence').useSetEvidenceDisclaimerModalOpen();

  const shouldPrompt =
    isEnabled &&
    !isRegionPending &&
    !isAccessPending &&
    isWorkEvidenceDisclaimerCohort({ isUKOrEU, isNhsUser }) &&
    disclaimer !== undefined &&
    !disclaimer.hasSeen &&
    !isAnotherDrawerOpen;

  useEffect(() => {
    if (shouldPrompt) openDisclaimer(true);
  }, [shouldPrompt, openDisclaimer]);
};

export const EvidenceChildRunInline = ({
  childAgentRunId,
  status,
  question,
  allowMount,
  renderReplyActions,
  searchesInWorkingBlock,
  onLiveItems,
  className,
}: EvidenceChildRunInlineProps): JSX.Element | null => {
  useWorkEvidenceDisclaimerPrompt();

  // Render, not call — keeps the hook from firing above its mount gate.
  if (!allowMount) {
    return (
      <StartingPlaceholder
        status={status}
        question={question}
        searchesInWorkingBlock={searchesInWorkingBlock}
        className={className}
      />
    );
  }
  return (
    <EvidenceChildRunLifecycle
      childAgentRunId={childAgentRunId}
      status={status}
      question={question}
      renderReplyActions={renderReplyActions}
      searchesInWorkingBlock={searchesInWorkingBlock}
      onLiveItems={onLiveItems}
      className={className}
    />
  );
};

interface EvidenceChildRunLifecycleProps {
  childAgentRunId: string;
  status: EvidenceSessionStatus;
  question?: string;
  renderReplyActions?: (copyableText: string | null) => ReactNode;
  searchesInWorkingBlock?: boolean;
  onLiveItems?: EvidenceChildRunInlineProps['onLiveItems'];
  className?: string;
}

const EvidenceChildRunLifecycle = ({
  childAgentRunId,
  status,
  question,
  renderReplyActions,
  searchesInWorkingBlock,
  onLiveItems,
  className,
}: EvidenceChildRunLifecycleProps): JSX.Element | null => {
  const intl = useIntl();
  const { mutateAsync: resolveApproval } = useMutateResolveApproval(agentHarnessClient);
  // Default per-turn follow: streams while the turn is active, hydrates from snapshot on reload.
  const { items, isLoading } = useRunStreamLifecycle(childAgentRunId, agentHarnessClient);
  const isStreaming = status === 'running';
  const registry = useMemo(() => buildRegistryFromItems(items), [items]);
  // While the transcript is still loading the saved snapshot is the better read.
  useEffect(() => {
    onLiveItems?.(childAgentRunId, items.length > 0 ? items : null);
  }, [onLiveItems, childAgentRunId, items]);
  useEffect(
    () => () => onLiveItems?.(childAgentRunId, null),
    [onLiveItems, childAgentRunId]
  );
  const resolveVisualDx = useCallback(
    async (
      approvalId: string,
      selections: VisualDxDiagnosticFlowSelections
    ): Promise<void> => {
      let failureReason: 'not_accepted' | 'request_failed' = 'request_failed';
      try {
        const response = await resolveApproval({
          agentRunId: childAgentRunId,
          approvalId,
          body: {
            verdict: 'approve',
            modified_input: { selections },
          },
        });
        if (!response.accepted) {
          failureReason = 'not_accepted';
          throw new Error('VisualDx selections were not accepted');
        }
      } catch (error) {
        logger.error('evidence_child_visualdx_resolve_failed', {
          childAgentRunId,
          reason: failureReason,
          error: sanitizeError(error),
        });
        toast({
          title: intl.formatMessage(visualDxSubmitErrorMessage),
          tone: 'destructive',
        });
        throw error;
      }
    },
    [childAgentRunId, intl, resolveApproval]
  );

  // Warm the partner-integrations cache `getPartnerConfig` reads imperatively during
  // citation render, else partner citations fall back to a favicon instead of the
  // branded logo. Collapsed to a 0/1 presence signal (not the raw timestamp) so
  // citations remount once branding lands, not on every background refetch — which
  // would otherwise close an open popover.
  const { dataUpdatedAt } = getWorkHostPort('evidence').useQueryEvidenceIntegrations();
  const partnerConfigVersion = dataUpdatedAt > 0 ? 1 : 0;

  if (items.length === 0) {
    // A finished child's transcript is still loading (a reload): it has not
    // been seen to produce nothing yet, so it says nothing until it arrives.
    if (isLoading && status === 'success') return null;
    return (
      <>
        <StartingPlaceholder
          status={status}
          question={question}
          isLoading={isLoading || isStreaming}
          searchesInWorkingBlock={searchesInWorkingBlock}
          className={className}
        />
        {!isLoading && !isStreaming ? renderReplyActions?.(null) : null}
      </>
    );
  }

  return (
    <div className={cn('flex w-full flex-col gap-2', className)}>
      <EvidenceChildTimeline
        items={items}
        registry={registry}
        isStreaming={isStreaming}
        partnerConfigVersion={partnerConfigVersion}
        onResolveVisualDx={resolveVisualDx}
        renderReplyActions={renderReplyActions}
        searchesInWorkingBlock={searchesInWorkingBlock}
      />
      {status === 'error' && (
        <ErrorNotice
          message={intl.formatMessage(projectRunFailureMessages.evidencePartialResults)}
        />
      )}
      {status === 'cancelled' && <CancelledNotice question={question} />}
    </div>
  );
};
